import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Edit2, Book, CheckCircle, XCircle, FileText } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';
export const AdminBookUploadPage = () => {
  // NAV STATE
  const [activeTab, setActiveTab] = useState('books');

  // BOOKS STATE (YOUR ORIGINAL STATE)
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { showToast, ToastContainer } = useToast();
  const [usersList, setUsersList] = useState([]);
  // Form state (YOUR ORIGINAL STATE)
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    pdfFile: null,
    attributionFile: null
  });

  const [previewUrls, setPreviewUrls] = useState({
    pdf: null,
    attribution: null
  });

  // SYSTEM INFO STATE
  const [systemInfo, setSystemInfo] = useState(null);
  const [systemLoading, setSystemLoading] = useState(false);

  // USER MANAGEMENT STATE
  const [userStats, setUserStats] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  // YOUR ORIGINAL USEEFFECT
  useEffect(() => {
    if (activeTab === 'books') {
      fetchBooks();
    } else if (activeTab === 'system') {
      fetchSystemInfo();
    } else if (activeTab === 'users') {
      fetchUserStats();
      fetchUsersList();
    }
  }, [activeTab]);

  // YOUR ORIGINAL fetchBooks FUNCTION
  const fetchBooks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/books`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch books');
      
      const data = await response.json();
      setBooks(data);
    } catch (error) {
      console.error('Error fetching books:', error);
      showToast('Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersList = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/list`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch users list');
      
      const data = await response.json();
      console.log(data);
      setUsersList(data.users);
      
    } catch (err) {
      console.error('Error fetching users list:', err);
      showToast('Failed to load users list');
    }
  };
  // NEW SYSTEM INFO FUNCTION
  const fetchSystemInfo = async () => {
    try {
      setSystemLoading(true);
      const response = await fetch(`${API_URL}/api/admin/system-info`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch system info');
      
      const data = await response.json();
      setSystemInfo(data);
    } catch (error) {
      console.error('Error fetching system info:', error);
      showToast('Failed to load system info');
    } finally {
      setSystemLoading(false);
    }
  };

  // NEW USER STATS FUNCTION
  const fetchUserStats = async () => {
    try {
      setUserLoading(true);
      const response = await fetch(`${API_URL}/api/admin/users/stats`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch user stats');
      
      const data = await response.json();
      setUserStats(data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
      showToast('Failed to load user stats');
    } finally {
      setUserLoading(false);
    }
  };

  // YOUR ORIGINAL handleInputChange FUNCTION
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // YOUR ORIGINAL handleFileChange FUNCTION
  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (fileType === 'pdf') {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        showToast('Please select a PDF file');
        return;
      }
      setFormData(prev => ({ ...prev, pdfFile: file }));
      setPreviewUrls(prev => ({ ...prev, pdf: URL.createObjectURL(file) }));
    } else if (fileType === 'attribution') {
      if (!file.name.toLowerCase().endsWith('.txt')) {
        showToast('Please select a TXT file');
        return;
      }
      setFormData(prev => ({ ...prev, attributionFile: file }));
      
      // Read text file content for preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewUrls(prev => ({ ...prev, attribution: event.target.result }));
      };
      reader.readAsText(file);
    }
  };

  // YOUR ORIGINAL handleSubmit FUNCTION
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.author || !formData.pdfFile ) {
      showToast('Please fill in all required fields');
      return;
    }

    setUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('author', formData.author);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('pdf_file', formData.pdfFile);
      formDataToSend.append('attribution_file', formData.attributionFile);

      const response = await fetch(`${API_URL}/api/admin/books`, {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload book');
      }

      showToast(`Book "${formData.title}" uploaded successfully!`);
      
      // Reset form
      setFormData({
        title: '',
        author: '',
        description: '',
        pdfFile: null,
        attributionFile: null
      });
      setPreviewUrls({ pdf: null, attribution: null });
      
      // Reset file inputs
      document.getElementById('pdf-input').value = '';
      document.getElementById('attribution-input').value = '';
      
      // Refresh books list
      fetchBooks();
    } catch (error) {
      console.error('Error uploading book:', error);
      showToast(error.message || 'Failed to upload book');
    } finally {
      setUploading(false);
    }
  };

  // YOUR ORIGINAL handleDeleteBook FUNCTION
  const handleDeleteBook = async (bookId, bookTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${bookTitle}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/books/${bookId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete book');

      showToast('Book deleted successfully');
      fetchBooks();
    } catch (error) {
      console.error('Error deleting book:', error);
      showToast('Failed to delete book');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <ToastContainer />
      
      {/* NAV BAR */}
      <nav style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '1rem 2rem',
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center'
      }}>
        <button
          onClick={() => setActiveTab('books')}
          style={{
            background: activeTab === 'books' ? 'white' : 'rgba(255, 255, 255, 0.1)',
            color: activeTab === 'books' ? '#667eea' : 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          📚 Books
        </button>
        <button
          onClick={() => setActiveTab('system')}
          style={{
            background: activeTab === 'system' ? 'white' : 'rgba(255, 255, 255, 0.1)',
            color: activeTab === 'system' ? '#667eea' : 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          🖥️ System Info
        </button>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            background: activeTab === 'users' ? 'white' : 'rgba(255, 255, 255, 0.1)',
            color: activeTab === 'users' ? '#667eea' : 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          👥 User Management
        </button>
      </nav>

      <div style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* BOOKS TAB - YOUR ORIGINAL CONTENT */}
          {activeTab === 'books' && (
            <>
              {/* Header */}
              <div style={{ 
                textAlign: 'center', 
                color: 'white', 
                marginBottom: '2rem' 
              }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  📚 Book Upload Admin
                </h1>
                <p style={{ fontSize: '1.125rem', opacity: 0.9 }}>
                  Upload and manage your book library
                </p>
              </div>

              {/* Upload Form */}
              <div style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                marginBottom: '2rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }}>
                <h2 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '600', 
                  marginBottom: '1.5rem',
                  color: '#1f2937'
                }}>
                  Upload New Book
                </h2>

                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    {/* Title */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: '600', 
                        marginBottom: '0.5rem',
                        color: '#374151'
                      }}>
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          fontSize: '1rem'
                        }}
                        placeholder="Enter book title"
                      />
                    </div>

                    {/* Author */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: '600', 
                        marginBottom: '0.5rem',
                        color: '#374151'
                      }}>
                        Author *
                      </label>
                      <input
                        type="text"
                        name="author"
                        value={formData.author}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          fontSize: '1rem'
                        }}
                        placeholder="Enter author name"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#374151'
                    }}>
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        minHeight: '100px',
                        resize: 'vertical'
                      }}
                      placeholder="Enter book description (optional)"
                    />
                  </div>

                  {/* File Uploads */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    {/* PDF Upload */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: '600', 
                        marginBottom: '0.5rem',
                        color: '#374151'
                      }}>
                        PDF File *
                      </label>
                      <div style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '0.5rem',
                        padding: '2rem',
                        textAlign: 'center',
                        background: '#f9fafb',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('pdf-input').click()}
                      >
                        <Upload size={32} color="#9ca3af" style={{ margin: '0 auto 0.5rem' }} />
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {formData.pdfFile ? formData.pdfFile.name : 'Click to upload PDF'}
                        </p>
                        <input
                          id="pdf-input"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleFileChange(e, 'pdf')}
                          style={{ display: 'none' }}
                        />
                      </div>
                      {formData.pdfFile && (
                        <div style={{ 
                          marginTop: '0.5rem', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          color: '#059669'
                        }}>
                          <CheckCircle size={16} />
                          <span style={{ fontSize: '0.875rem' }}>PDF uploaded</span>
                        </div>
                      )}
                    </div>

                    {/* Attribution Upload */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: '600', 
                        marginBottom: '0.5rem',
                        color: '#374151'
                      }}>
                        Attribution File (.txt)
                      </label>
                      <div style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '0.5rem',
                        padding: '2rem',
                        textAlign: 'center',
                        background: '#f9fafb',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('attribution-input').click()}
                      >
                        <FileText size={32} color="#9ca3af" style={{ margin: '0 auto 0.5rem' }} />
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {formData.attributionFile ? formData.attributionFile.name : 'Click to upload attribution'}
                        </p>
                        <input
                          id="attribution-input"
                          type="file"
                          accept=".txt"
                          onChange={(e) => handleFileChange(e, 'attribution')}
                          style={{ display: 'none' }}
                        />
                      </div>
                      {formData.attributionFile && (
                        <div style={{ 
                          marginTop: '0.5rem', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          color: '#059669'
                        }}>
                          <CheckCircle size={16} />
                          <span style={{ fontSize: '0.875rem' }}>Attribution uploaded</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preview Attribution Content */}
                  {previewUrls.attribution && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: '600', 
                        marginBottom: '0.5rem',
                        color: '#374151'
                      }}>
                        Attribution Preview
                      </label>
                      <div style={{
                        padding: '1rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        color: '#4b5563',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {previewUrls.attribution}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={uploading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: uploading ? '#9ca3af' : '#9333ea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Upload size={20} />
                    {uploading ? 'Uploading...' : 'Upload Book'}
                  </button>
                </form>
              </div>

              {/* Books List */}
              <div style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }}>
                <h2 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '600', 
                  marginBottom: '1.5rem',
                  color: '#1f2937'
                }}>
                  Uploaded Books ({books.length})
                </h2>

                {loading ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                    Loading books...
                  </p>
                ) : books.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                    No books uploaded yet. Upload your first book above!
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {books.map(book => (
                      <div
                        key={book.id}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          padding: '1.5rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          background: '#fafafa'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <h3 style={{ 
                            fontSize: '1.25rem', 
                            fontWeight: '600', 
                            color: '#1f2937',
                            marginBottom: '0.25rem'
                          }}>
                            {book.title}
                          </h3>
                          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                            by {book.author}
                          </p>
                          {book.description && (
                            <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
                              {book.description}
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                            <a 
                              href={book.pdf_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#9333ea', textDecoration: 'none' }}
                            >
                              View PDF
                            </a>
                            <a 
                              href={book.attribution_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#9333ea', textDecoration: 'none' }}
                            >
                              View Attribution
                            </a>
                            <span>Uploaded: {new Date(book.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteBook(book.id, book.title)}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* SYSTEM INFO TAB */}
          {activeTab === 'system' && (
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '2rem',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                  🖥️ System Information
                </h2>
                <button
                  onClick={fetchSystemInfo}
                  disabled={systemLoading}
                  style={{
                    background: '#9333ea',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: systemLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {systemLoading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>

              {systemLoading ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                  Loading system info...
                </p>
              ) : systemInfo ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                  {/* CPU */}
                  <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>💻 CPU</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Usage:</span>
                      <strong>{systemInfo.system.cpu.usage_percent}%</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Cores:</span>
                      <strong>{systemInfo.system.cpu.core_count}</strong>
                    </div>
                    {systemInfo.system.cpu.frequency_mhz && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                        <span>Frequency:</span>
                        <strong>{systemInfo.system.cpu.frequency_mhz} MHz</strong>
                      </div>
                    )}
                    <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden', marginTop: '1rem' }}>
                      <div style={{ width: `${systemInfo.system.cpu.usage_percent}%`, height: '100%', background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }} />
                    </div>
                  </div>

                  {/* Memory */}
                  <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>🧠 Memory</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Used:</span>
                      <strong>{systemInfo.system.memory.used_gb} GB</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Available:</span>
                      <strong>{systemInfo.system.memory.available_gb} GB</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Total:</span>
                      <strong>{systemInfo.system.memory.total_gb} GB</strong>
                    </div>
                    <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden', marginTop: '1rem' }}>
                      <div style={{ width: `${systemInfo.system.memory.usage_percent}%`, height: '100%', background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }} />
                    </div>
                  </div>

                  {/* Disk */}
                  <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>💾 Disk</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Used:</span>
                      <strong>{systemInfo.system.disk.used_gb} GB</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Free:</span>
                      <strong>{systemInfo.system.disk.free_gb} GB</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Total:</span>
                      <strong>{systemInfo.system.disk.total_gb} GB</strong>
                    </div>
                    <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden', marginTop: '1rem' }}>
                      <div style={{ width: `${systemInfo.system.disk.usage_percent}%`, height: '100%', background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }} />
                    </div>
                  </div>

                  {/* Database */}
                  <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>🗄️ Database</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Books:</span>
                      <strong>{systemInfo.database.total_books}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Premium Books:</span>
                      <strong>{systemInfo.database.total_premium_books}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Users:</span>
                      <strong>{systemInfo.database.total_users}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Families:</span>
                      <strong>{systemInfo.database.total_families}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                      <span>Kids:</span>
                      <strong>{systemInfo.database.total_kids}</strong>
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                  No system information available. Click Refresh to load.
                </p>
              )}
            </div>
          )}

          {/* USER MANAGEMENT TAB */}
{activeTab === 'users' && (
  <div style={{
    background: 'white',
    borderRadius: '1rem',
    padding: '2rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
        👥 User Management
      </h2>
      <button
        onClick={fetchUserStats}
        disabled={userLoading}
        style={{
          background: '#9333ea',
          color: 'white',
          border: 'none',
          padding: '0.75rem 1.5rem',
          borderRadius: '0.5rem',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: userLoading ? 'not-allowed' : 'pointer'
        }}
      >
        {userLoading ? 'Loading...' : '🔄 Refresh'}
      </button>
    </div>

    {userLoading ? (
      <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
        Loading user stats...
      </p>
    ) : userStats ? (
      <>
        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: '#6b7280' }}>👥 Total Users</h3>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#667eea' }}>{userStats.users.total}</div>
          </div>

          <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: '#6b7280' }}>📈 Recent Registrations</h3>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#667eea' }}>{userStats.users.recent_registrations_30d}</div>
            <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Last 30 days</div>
          </div>

          <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: '#6b7280' }}>✨ Active Users</h3>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#667eea' }}>{userStats.users.active_users_7d}</div>
            <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Last 7 days</div>
          </div>

          <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: '#6b7280' }}>👨‍👩‍👧‍👦 Families</h3>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#667eea' }}>{userStats.families.total}</div>
            <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Avg size: {userStats.families.average_size}</div>
          </div>
        </div>

        {/* Users by Role */}
        {userStats.users.by_role && (
          <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb', marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>Users by Role</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
              {Object.entries(userStats.users.by_role).map(([role, count]) => (
                <div key={role} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'white', borderRadius: '0.5rem' }}>
                  <span style={{ fontWeight: '600', color: '#1f2937', textTransform: 'capitalize' }}>{role}</span>
                  <span style={{ background: '#667eea', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.875rem', fontWeight: '600' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ADD THIS USERS TABLE */}
        <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>All Users</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'white' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Role</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Joined</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {usersList.map((user) => (
                  <tr key={user.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1f2937' }}>
                      {user.first_name} {user.last_name}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1f2937' }}>{user.email}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        background: user.role === 'admin' ? '#f3e8ff' : '#dbeafe',
                        color: user.role === 'admin' ? '#7c3aed' : '#2563eb'
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {user.last_seen ? new Date(user.last_seen).toLocaleDateString() : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    ) : (
      <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
        No user statistics available. Click Refresh to load.
      </p>
    )}
  </div>
)}

        </div>
      </div>
    </div>
  );
};
